// Arena · Cutdown helpers (contracts/phase4.md §A, contracts/realtime/README.md §5): the cut curve,
// and the round-by-round replay derived from a match's results payload for the admin viewer.

import type { ArenaAnswer, ArenaMatch, ArenaPlacement, ArenaRound } from "../api/schemas";

/** Survivor share after each cut round: 100 → 50 → 32 → 20 → 12 → 7 → 4 → 2. */
export const CUT_FRACTIONS = [0.5, 0.32, 0.2, 0.12, 0.07, 0.04, 0.02] as const;

/** Survivors of cut round r (1-based) for n starters: max(2, round(n × fraction)), rounding half up. */
export function survivors(n: number, r: number): number {
  if (r < 1) return n;
  if (r > CUT_FRACTIONS.length) return 2;
  return Math.max(2, Math.floor(n * CUT_FRACTIONS[r - 1]! + 0.5));
}

/** Cut rounds before the final two. */
export function cutRounds(n: number): number {
  if (n <= 2) return 0;
  for (let r = 1; ; r++) if (survivors(n, r) <= 2) return r;
}

/** Answers under this many ms after RoundGo are flagged (contracts/phase4.md "Anti-cheat"). */
export const FLAG_MS = 250;
/** A round is open for at most 20 s. */
export const ROUND_MS = 20_000;

export const OPERATION_LABEL: Record<string, string> = {
  add: "Add",
  subtract: "Subtract",
  multiply: "Multiply",
  divide: "Divide",
  square: "Square",
};
export const operationLabel = (op: string | null) => (op ? (OPERATION_LABEL[op] ?? op.replace(/_/g, " ")) : "—");

export const REASON_LABEL: Record<ArenaPlacement["reason"], string> = {
  winner: "winner",
  final: "runner-up",
  cut: "cut",
  forfeit: "forfeit",
  excluded: "excluded",
  aborted: "aborted",
};

/** Per-seat facts for the dot field. */
export type SeatState = "alive" | "through" | "cut" | "out" | "winner" | "empty";

export interface ReplayRound {
  round: ArenaRound;
  /** 0-based position in the match's rounds. */
  index: number;
  /** "Round 3 of 8", "Final · point 2". */
  label: string;
  /** Seats in play when the round opened. */
  aliveBefore: Set<number>;
  /** Seats cut when it closed. */
  cut: Set<number>;
  /** Seats still in afterwards. */
  aliveAfter: Set<number>;
  answers: ArenaAnswer[];
  /** Alive seats with no answer. */
  missing: number[];
  correct: number;
  wrong: number;
  flagged: ArenaAnswer[];
  /** Median correct answer time. */
  medianMs: number | null;
  /** The slowest correct answer that survived (the cut line), when the round cut anyone. */
  cutLineMs: number | null;
  /** Open time: closedAt − goAt. */
  durationMs: number | null;
}

export interface Replay {
  /** Seats in dot order (sorted). */
  seats: number[];
  bySeat: Map<number, ArenaPlacement>;
  rounds: ReplayRound[];
  finalists: number[];
}

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : Math.round((s[m - 1]! + s[m]!) / 2);
};

/** Builds the per-round view: who was in, who answered, who got cut, the cut line and the flags. */
export function buildReplay(match: ArenaMatch): Replay {
  const bySeat = new Map(match.placements.map((p) => [p.seat, p]));
  const seatSet = new Set<number>(match.placements.map((p) => p.seat));
  for (const r of match.rounds) {
    for (const a of r.answers) seatSet.add(a.seat);
    for (const c of r.cut) seatSet.add(c);
  }
  if (seatSet.size === 0) for (let i = 0; i < match.players; i++) seatSet.add(i);
  const seats = [...seatSet].sort((a, b) => a - b);
  let alive = new Set(seats);
  const cutTotal = match.cutRounds ?? match.rounds.filter((r) => !r.final).length;
  const of = cutTotal + (match.rounds.some((r) => r.final) ? 1 : 0);
  const rounds: ReplayRound[] = [];
  let finalists: number[] = [];
  match.rounds.forEach((round, index) => {
    const aliveBefore = new Set(alive);
    if (round.final && finalists.length === 0) finalists = [...aliveBefore];
    const cut = new Set(round.cut);
    const aliveAfter = new Set([...aliveBefore].filter((s) => !cut.has(s)));
    const answered = new Set(round.answers.map((a) => a.seat));
    const correctTimes = round.answers.filter((a) => a.correct).map((a) => a.answerMs);
    const survivingCorrect = round.answers.filter((a) => a.correct && !cut.has(a.seat)).map((a) => a.answerMs);
    const go = round.goAt ? Date.parse(round.goAt) : NaN;
    const closed = round.closedAt ? Date.parse(round.closedAt) : NaN;
    rounds.push({
      round,
      index,
      label: round.final ? `Final · point ${round.point ?? index + 1 - cutTotal}` : `Round ${round.displayRound ?? round.round} of ${of || "?"}`,
      aliveBefore,
      cut,
      aliveAfter,
      answers: [...round.answers].sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9) || a.answerMs - b.answerMs),
      missing: [...aliveBefore].filter((s) => !answered.has(s)).sort((a, b) => a - b),
      correct: correctTimes.length,
      wrong: round.answers.length - correctTimes.length,
      flagged: round.answers.filter((a) => a.flagged),
      medianMs: median(correctTimes),
      // The final has no cut line: a point goes to the faster correct answer.
      cutLineMs: !round.final && cut.size && survivingCorrect.length ? Math.max(...survivingCorrect) : null,
      durationMs: Number.isFinite(go) && Number.isFinite(closed) ? Math.max(0, closed - go) : null,
    });
    alive = aliveAfter;
  });
  return { seats, bySeat, rounds, finalists };
}

/** The dot for `seat` in round `r`: in play and through, cut this round, already out, or the winner. */
export function seatState(replay: Replay, r: ReplayRound, seat: number, winnerSeat: number | null): SeatState {
  if (!r.aliveBefore.has(seat)) return "out";
  if (r.cut.has(seat)) return "cut";
  if (r.round.final && winnerSeat === seat && r.index === replay.rounds.length - 1) return "winner";
  return "through";
}

export interface HistogramBin {
  from: number;
  to: number;
  correct: number;
  wrong: number;
  flagged: number;
}

/** Answer times in `binMs` bins up to the slowest answer (or 20 s), correct and wrong stacked. */
export function answerHistogram(answers: ArenaAnswer[], binMs = 500): HistogramBin[] {
  const max = Math.min(ROUND_MS, Math.max(binMs * 4, ...answers.map((a) => a.answerMs + 1)));
  const n = Math.ceil(max / binMs);
  const bins: HistogramBin[] = Array.from({ length: n }, (_, i) => ({ from: i * binMs, to: (i + 1) * binMs, correct: 0, wrong: 0, flagged: 0 }));
  for (const a of answers) {
    const b = bins[Math.min(n - 1, Math.floor(a.answerMs / binMs))]!;
    if (a.correct) b.correct++;
    else b.wrong++;
    if (a.flagged) b.flagged++;
  }
  return bins;
}

/** 812 → "0.81 s", 12_400 → "12.4 s". */
export function fmtSeconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return ms < 10_000 ? `${(ms / 1000).toFixed(2)} s` : `${(ms / 1000).toFixed(1)} s`;
}

/** "12:00" list → the next `count` drop instants (UTC) after `from`. */
export function nextDrops(schedule: string[], from: Date, count = 3): Date[] {
  const times = [...new Set(schedule)].filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)).sort();
  if (!times.length) return [];
  const out: Date[] = [];
  const day = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  for (let d = 0; out.length < count && d < 8; d++) {
    for (const t of times) {
      const [h, m] = t.split(":").map(Number) as [number, number];
      const at = new Date(day + d * 86_400_000 + h * 3_600_000 + m * 60_000);
      if (at.getTime() > from.getTime()) out.push(at);
      if (out.length === count) break;
    }
  }
  return out;
}
